// ReportFilterBar.js
// Shared filter bar for report views: a period selector (with computed
// date ranges) plus an optional department filter, gated per-report via
// showDepartmentFilter since not every report supports narrowing by
// department. Emits a normalized {period, startDate, endDate, departmentId}
// object on every change — reports just consume startDate/endDate/departmentId.

import React, { useState, useEffect } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../DatePicker.css';

const PERIOD_OPTIONS = [
  { value: 'all_time', label: 'All Time' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' },
];

function toDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function computeRange(period) {
  const now = new Date();
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  switch (period) {
    case 'all_time':
      return [null, null];
    case 'this_month':
      return [thisMonthStart, thisMonthEnd];
    case 'last_3_months':
      return [new Date(now.getFullYear(), now.getMonth() - 2, 1), thisMonthEnd];
    case 'this_year':
      return [new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear(), 11, 31)];
    case 'last_year':
      return [new Date(now.getFullYear() - 1, 0, 1), new Date(now.getFullYear() - 1, 11, 31)];
    default:
      return [thisMonthStart, thisMonthEnd];
  }
}

function ReportFilterBar({ value, onChange, showDepartmentFilter, departments = [], departmentLocked = false }) {
  const [period, setPeriod] = useState(value?.period || 'this_month');
  const [customRange, setCustomRange] = useState([null, null]);

  useEffect(() => {
    if (period === 'custom') return; // wait for an explicit range pick
    const [start, end] = computeRange(period);
    onChange({
      period,
      startDate: start ? toDateString(start) : null,
      endDate: end ? toDateString(end) : null,
      departmentId: value?.departmentId ?? '',
    });
    // Only recompute when the period changes — including value/onChange
    // here would re-fire on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const handleCustomRangeChange = (dates) => {
    const [start, end] = dates;
    setCustomRange(dates);
    if (start && end) {
      onChange({
        period: 'custom',
        startDate: toDateString(start),
        endDate: toDateString(end),
        departmentId: value?.departmentId ?? '',
      });
    }
  };

  const handleDepartmentChange = (e) => {
    onChange({ ...value, departmentId: e.target.value });
  };

  return (
    <Row className="align-items-end g-3 mb-3 no-print">
      <Col md={showDepartmentFilter ? 4 : 6}>
        <Form.Group>
          <Form.Label>Period</Form.Label>
          <Form.Control as="select" value={period} onChange={(e) => setPeriod(e.target.value)}>
            {PERIOD_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Control>
        </Form.Group>
      </Col>
      {period === 'custom' && (
        <Col md={showDepartmentFilter ? 4 : 6}>
          <Form.Group>
            <Form.Label>Date Range</Form.Label>
            <div>
              <DatePicker
                dateFormat="dd/MM/yyyy"
                onChange={handleCustomRangeChange}
                startDate={customRange[0]}
                endDate={customRange[1]}
                selectsRange
                isClearable={false}
              />
            </div>
          </Form.Group>
        </Col>
      )}
      {showDepartmentFilter && !departmentLocked && (
        <Col md={4}>
          <Form.Group>
            <Form.Label>Department</Form.Label>
            <Form.Control as="select" value={value?.departmentId || ''} onChange={handleDepartmentChange}>
              <option value="">All Departments</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>
                  {d.company_name ? `${d.company_name} — ${d.name}` : d.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
      )}
    </Row>
  );
}

export default ReportFilterBar;
